// src/components/CrimeRecords.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllCrimes } from "../features/crimeSlice";
import { useSnackbar } from "notistack";
import DynamicBreadcrumbs from "../components/layout/Breadcrumb";
import Layout from "../components/layout/Layout";
import locationData from "../services/up-area.json";

const CrimeRecords = () => {
  const [district, setDistrict] = useState("");
  const [tehsil, setTehsil] = useState("");
  const [policeStation, setPoliceStation] = useState("");
  const [tehsilOptions, setTehsilOptions] = useState([]);
  const [policeStationOptions, setPoliceStationOptions] = useState([]);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { crimeRecords, status, error } = useSelector((state) => state.crime);
  const isLoading = status === "loading";

  // Safe handling: expect array of incidents
  const incidents = Array.isArray(crimeRecords) ? crimeRecords : [];

  // Status colors
  const statusColors = {
    pending: "default",
    inCourt: "error",
    trial: "warning",
    atPoliceStation: "info",
    clear: "success",
  };

  useEffect(() => {
    if (district) {
      const selected = locationData.find((d) => d.district === district);
      setTehsilOptions(selected?.tehsils?.map((t) => t.name) || []);
      setTehsil("");
      setPoliceStation("");
      setPoliceStationOptions([]);
    }
  }, [district]);

  useEffect(() => {
    if (tehsil && district) {
      const selectedDistrict = locationData.find(
        (d) => d.district === district
      );
      const selectedTehsil = selectedDistrict?.tehsils?.find(
        (t) => t.name === tehsil
      );
      setPoliceStationOptions(selectedTehsil?.thanas || []);
      setPoliceStation("");
    }
  }, [tehsil, district]);

  const handleSearch = async () => {
    try {
      await dispatch(
        fetchAllCrimes({ district, tehsil, policeStation })
      ).unwrap();
      enqueueSnackbar("Incidents loaded successfully", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to load incidents", {
        variant: "error",
      });
    }
  };

  const handleReset = () => {
    setDistrict("");
    setTehsil("");
    setPoliceStation("");
  };

  return (
    <Layout
      title="Incident Records"
      description="Uttar Pradesh Police Crime Records"
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <DynamicBreadcrumbs />

        <Typography
          variant="h4"
          component="h1"
          align="center"
          gutterBottom
          sx={{ fontWeight: 700, color: "primary.dark", mb: 4 }}
        >
          All Registered Incidents
        </Typography>

        {/* Filters - Modern Card */}
        <Paper
          elevation={4}
          sx={{ p: 4, mb: 5, borderRadius: 3, bgcolor: "background.paper" }}
        >
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="District"
                value={district}
                onChange={(e) => setDistrict(e.target.value)}
                variant="outlined"
                size="medium"
              >
                {locationData.map((item) => (
                  <MenuItem key={item.district} value={item.district}>
                    {item.district}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Tehsil"
                value={tehsil}
                onChange={(e) => setTehsil(e.target.value)}
                disabled={!district}
                variant="outlined"
              >
                {tehsilOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Police Station"
                value={policeStation}
                onChange={(e) => setPoliceStation(e.target.value)}
                disabled={!tehsil}
                variant="outlined"
              >
                {policeStationOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}
            >
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                disabled={isLoading}
              >
                Reset Filters
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  isLoading ? <CircularProgress size={20} /> : <SearchIcon />
                }
                onClick={handleSearch}
                disabled={isLoading}
                size="large"
              >
                {isLoading ? "Searching..." : "Search Incidents"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 4 }}
            onClose={() => dispatch({ type: "crime/clearError" })}
          >
            {error}
          </Alert>
        )}

        {/* Results */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress size={70} thickness={4} />
          </Box>
        ) : incidents.length === 0 ? (
          <Card
            sx={{
              textAlign: "center",
              py: 8,
              bgcolor: "grey.100",
              borderRadius: 3,
            }}
          >
            <CardContent>
              <Typography variant="h5" color="text.secondary" gutterBottom>
                No incidents found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Try different filters or register new incidents.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Paper elevation={4} sx={{ borderRadius: 3, overflow: "hidden" }}>
            <TableContainer>
              <Table sx={{ minWidth: 900 }}>
                <TableHead sx={{ bgcolor: "primary.dark" }}>
                  <TableRow>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Incident ID
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Incident Status
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Crime Type
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Offenders
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Police Station
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Date
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {incidents.map((incident) => (
                    <TableRow
                      key={incident._id}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <TableCell>
                        <strong style={{ color: "#d32f2f" }}>
                          {incident.incidentId}
                        </strong>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={incident.incidentStatus}
                          color={statusColors[incident.incidentStatus]}
                          size="medium"
                          sx={{ fontWeight: 600, mt: 1 }}
                        />
                      </TableCell>
                      <TableCell>{incident.crimeType}</TableCell>
                      <TableCell>
                        {incident.offenders?.length > 0 ? (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {incident.offenders.map((off) => (
                              <Chip
                                key={off.crimeNumber}
                                label={`${
                                  off.personId?.firstName || "Unknown"
                                } ${off.personId?.lastName || ""} (${
                                  off.crimeNumber
                                })`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No offenders listed
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{incident.policeStation}</TableCell>
                      <TableCell>
                        {new Date(incident.crimeDate).toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          }
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          component={RouterLink}
                          to={`/dashboard/view-incidents/${incident.incidentId}`}
                          variant="outlined"
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Container>
    </Layout>
  );
};

export default CrimeRecords;
